//
// A wizard's steps as one chevron bar: steps already done in the light blue of
// ProgressBar with a check mark, the current one in its dark blue naming the
// step, the remaining ones grey and unnamed.
//
import classNames from 'classnames';
import PropTypes from 'prop-types';

import SvgColorImg from './SvgColorImg';

// Blues of ProgressBar, so every progress display reads the same
const doneColor = '#95d3e5';
const currentColor = 'var(--color-pxMedBlue)';
const futureColor = '#e8e8e8';

// A card points into the next one and is notched on the left to receive the
// previous point, so the two edges of a junction are parallel. The card then
// slides back over its predecessor by all but the separator: what is left
// white between them is that width, at the point as well as along the slopes.
// The row's two ends are round: nothing points out of the bar.
const arrowPx = 10;
const separatorPx = 6;

const stepClipPath = (isFirst, isLast) => {
  const rightEdge = (isLast)
    ? '100% 0, 100% 100%'
    : `calc(100% - ${arrowPx}px) 0, 100% 50%, calc(100% - ${arrowPx}px) 100%`;
  const leftNotch = (isFirst) ? '' : `, ${arrowPx}px 50%`;

  return `polygon(0 0, ${rightEdge}, 0 100%${leftNotch})`;
};

function WizardStepsBar(props) {
  const {
    steps,
    currentKey,
    narrow = false,
  } = props;

  const currentIndex = steps.findIndex((step) => (step.key === currentKey));
  const currentLbl = steps[currentIndex]?.lbl;

  const barContent = (
    <div className="w-full flex flex-row h-8 px-6 overflow-hidden">
      {steps.map((step, index) => {
        let backgroundColor = futureColor;
        if (index < currentIndex) backgroundColor = doneColor;
        else if (index === currentIndex) backgroundColor = currentColor;

        const isFirst = (index === 0);
        const isLast = (index === (steps.length - 1));

        return (
          <div
            key={step.key}
            className={classNames(
              'flex items-center justify-center',
              {
                // The named step takes the width its label needs (px-5 clears the notch and the
                // point), the unnamed ones share what is left
                'flex-auto min-w-0 px-5': (index === currentIndex),
                'flex-1': (index !== currentIndex),
                'rounded-l-full': isFirst,
                'rounded-r-full': isLast,
              },
            )}
            style={{
              backgroundColor,
              clipPath: stepClipPath(isFirst, isLast),
              marginLeft: (isFirst) ? 0 : `${separatorPx - arrowPx}px`,
            }}
          >
            {(index < currentIndex)
              ? (
                  <SvgColorImg
                    src="/images/svg/gui/check.svg"
                    imgColor="#ffffff"
                    className={(narrow) ? 'h-5 w-5' : 'h-7 w-7'}
                  />
                )
              : null}
            {(index === currentIndex)
              ? (
                  <span className="text-white font-bold text-base truncate">
                    {(narrow) ? `${currentIndex + 1}/${steps.length}` : step.lbl}
                  </span>
                )
              : null}
          </div>
        );
      })}
    </div>
  );

  if (!narrow) {
    return (
      <div className="w-full shrink-0 mt-2 mb-4">
        {barContent}
      </div>
    );
  }

  // A chevron is too narrow for a step name on a phone: it carries the rank instead
  return (
    <div className="w-full shrink-0 mt-2 mb-3">
      {barContent}
      <div className="px-6 pt-1 text-center text-base font-bold text-pxTxt">
        {currentLbl}
      </div>
    </div>
  );
}

WizardStepsBar.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    lbl: PropTypes.string,
  })).isRequired,
  currentKey: PropTypes.string.isRequired,
  narrow: PropTypes.bool,
};

export default WizardStepsBar;
